import { css } from '@emotion/react'
import { Button, ButtonGroup, Cell, Grid, Heading, HFlow, Theme, useTheme, VFlow } from 'bold-ui'
import { green } from 'bold-ui/lib/styles/colors'
import { Box } from 'components/Box'
import { CheckboxField } from 'components/form'
import { AgeRangeField } from 'components/form/field/AgeRangeField'
import { AgeRangeType, RiscoVulnerabilidadeIvcfEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'
import { IvcfSubgrupoSelectField } from 'view/acompanhamento/acompanhamento-condicao-saude/components/filter/IvcfSubgrupoSelectField'
import { IVCF_RESULTADOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'
import { filterCheckboxLabelRender } from 'view/atendimentos/list/filter/ListaAtendimentoFilterPopperForm'

import { IvcfFilterModel } from '../../model/model-acompanhamentoCondicaoSaude'

interface IvcfFilterSectionProps<T> {
  meta: MetaRoot<T>
}

export function IvcfFilterSection<T extends IvcfFilterModel>(props: IvcfFilterSectionProps<T>) {
  const { meta } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value: buscaPorPontuacao, onChange: onChangeBuscaPorPontuacao },
  } = useField<IvcfFilterModel['buscaPorPontuacao']>(meta.buscaPorPontuacao.absolutePath(), {})

  const {
    input: { value: exibirApenasCidadaosIdososSemIvcf },
  } = useField<IvcfFilterModel['exibirApenasCidadaosIdososSemIvcf']>(
    meta.exibirApenasCidadaosIdososSemIvcf.absolutePath()
  )

  return (
    <VFlow>
      <Heading level={3}>IVCF-20</Heading>
      <Grid alignItems='center'>
        <Cell size={12}>
          <ButtonGroup>
            <Button
              kind={!buscaPorPontuacao ? 'primary' : 'normal'}
              size='small'
              onClick={() => onChangeBuscaPorPontuacao(false)}
            >
              Índice
            </Button>
            <Button
              kind={buscaPorPontuacao ? 'primary' : 'normal'}
              size='small'
              onClick={() => onChangeBuscaPorPontuacao(true)}
            >
              Pontuação
            </Button>
          </ButtonGroup>
        </Cell>
        <Cell size={6}>
          <Box>
            {!buscaPorPontuacao ? (
              <HFlow>
                <CheckboxField
                  name={meta.riscoVulnerabilidade.absolutePath()}
                  label={filterCheckboxLabelRender(
                    RiscoVulnerabilidadeIvcfEnum.BAIXO.capitalize(),
                    styles.indiceBaixoIvcf
                  )}
                  value={RiscoVulnerabilidadeIvcfEnum.BAIXO}
                  style={styles.indiceCheckbox}
                  disabled={exibirApenasCidadaosIdososSemIvcf}
                />
                <CheckboxField
                  name={meta.riscoVulnerabilidade.absolutePath()}
                  label={filterCheckboxLabelRender(
                    RiscoVulnerabilidadeIvcfEnum.MODERADO.capitalize(),
                    styles.indiceModeradoIvcf
                  )}
                  value={RiscoVulnerabilidadeIvcfEnum.MODERADO}
                  style={styles.indiceCheckbox}
                  disabled={exibirApenasCidadaosIdososSemIvcf}
                />
                <CheckboxField
                  name={meta.riscoVulnerabilidade.absolutePath()}
                  label={filterCheckboxLabelRender(
                    RiscoVulnerabilidadeIvcfEnum.ALTO.capitalize(),
                    styles.indiceAltoIvcf
                  )}
                  value={RiscoVulnerabilidadeIvcfEnum.ALTO}
                  disabled={exibirApenasCidadaosIdososSemIvcf}
                />
              </HFlow>
            ) : (
              <AgeRangeField
                name={meta.pontuacao.absolutePath()}
                rangeTypeOptions={[AgeRangeType.ANOS]}
                firstInputPlaceholder='Pontuação mín.'
                secondInputPlaceholder='Pontuação máx.'
                defaultOptionLabel='Pontos'
                disabled={exibirApenasCidadaosIdososSemIvcf}
              />
            )}
          </Box>
        </Cell>
        <Cell size={6}>
          <CheckboxField
            name={meta.exibirApenasCidadaosIdososSemIvcf.absolutePath()}
            label='Exibir apenas pessoas 60+ sem registro do IVCF-20'
          />
        </Cell>
        <Cell size={6}>
          <IvcfSubgrupoSelectField
            name={meta.subgrupo.absolutePath()}
            label='Dimensões alteradas'
            disabled={exibirApenasCidadaosIdososSemIvcf}
          />
        </Cell>
        <Cell size={6} />
      </Grid>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    border-color: ${green.c40};
  `,
  indiceCheckbox: css`
    margin-right: 0.5rem;
  `,
  indiceBaixoIvcf: css`
    background: ${IVCF_RESULTADOS.BAIXO.textColor};
  `,
  indiceModeradoIvcf: css`
    background: ${IVCF_RESULTADOS.MODERADO.lineBgColor};
  `,
  indiceAltoIvcf: css`
    background: ${IVCF_RESULTADOS.ALTO.textColor};
  `,
  divider: css`
    margin: 0;
    background-color: ${theme.pallete.divider};
  `,
})
